import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  login, loginWithPasskey, loginWithPasskeyAutofill, passkeyAutofillSupported, stopPasskeyAutofill, isAuthenticated, completeProfile
} from '../services/authService';
import { isPasskeySupported } from '../services/passkeyService';
import { useModal } from '../components/useModal';
import ProfileCompleteModal from '../components/ProfileCompleteModal';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import dataService from '../services/dataService';
import apiService from '../services/apiService';
import { usePersistentInput } from '../hooks/usePersistentInput';
import { isPushSupported, wasPermissionAsked, subscribeToPush, getNotificationPermission } from '../services/pushService';
import { requestSyncAfterLogin } from '../contexts/SyncContext';
import { useDesign } from '../utils/appearance';
import LoginView from './modern/LoginView';
import './LoginPage.css';

export default function LoginPage() {
  const [username, setUsername] = usePersistentInput('loginPage_username', '');
  const [password, setPassword] = useState(''); // Passwort NIEMALS im localStorage speichern!
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess, showWarning, ModalComponent } = useModal();
  const modern = useDesign() === 'modern';
  // Neues Design: Fehler direkt im Formular, Fortschritt der ersten Synchronisierung sichtbar
  const [loginError, setLoginError] = useState('');
  const [syncProgress, setSyncProgress] = useState(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const passkeyAvailable = isPasskeySupported();
  // Passkey-Vorschläge im E-Mail-Feld; ruht, solange der Passkey-Knopf benutzt wird
  const [autofillRun, setAutofillRun] = useState(0);
  const passkeyButtonActive = useRef(false);

  useEffect(() => {
    checkAuth();
    
    // Zeige Session-Ablauf-Nachricht wenn vorhanden
    if (location.state?.message) {
      showWarning('Sitzung abgelaufen', location.state.message);
    }

    // Zeige Deaktivierungs-Nachricht wenn vorhanden
    const params = new URLSearchParams(location.search);
    if (params.get('deactivated') === 'true') {
      showError('Account deaktiviert', 'Ihr Account wurde deaktiviert. Bitte kontaktieren Sie einen Administrator.');
    }

    // Online/Offline Event Listener
    const handleOnline = () => {
      setIsOnline(true);
      // Automatisch Seite neu laden wenn wieder online
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    };
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const checkAuth = async () => {
    const authenticated = await isAuthenticated();
    if (authenticated) {
      navigate('/');
    }
    setCheckingAuth(false);
  };

  // Nach erfolgreicher Anmeldung (Passwort oder Passkey)
  const handleLoginSuccess = (result) => {
    // Prüfe ob Profil vollständig ist
    if (!result.profileComplete) {
      showSuccess('Willkommen!', 'Bitte vervollständigen Sie Ihr Profil');
      setLoading(false);
      setShowProfileModal(true);
      return;
    }

    showSuccess('Willkommen!', `Angemeldet als ${result.user.username}`);
    
    // Push-Benachrichtigungen anfragen wenn unterstützt und noch nicht gefragt
    if (isPushSupported() && !wasPermissionAsked() && getNotificationPermission() === 'default') {
      setTimeout(async () => {
        const pushResult = await subscribeToPush();
        if (pushResult.success) {
          console.log('✅ Push-Benachrichtigungen aktiviert');
        }
      }, 1500);
    }

    // Synchronisation läuft nach dem Wechsel in die App im Hintergrund (SyncContext)
    requestSyncAfterLogin();
    navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoginError('');
    if (!username.trim() || !password.trim()) {
      if (modern) setLoginError('Bitte E-Mail und Passwort eingeben.');
      else showError('Fehlende Eingaben', 'Bitte geben Sie Benutzername und Passwort ein');
      return;
    }

    setLoading(true);
    setSyncProgress(null);

    try {
      const result = await login(username, password);
      
      if (result.success) {
        handleLoginSuccess(result);
      } else {
        if (modern) setLoginError(result.error || 'E-Mail oder Passwort ist falsch.');
        else showError('Anmeldung fehlgeschlagen', result.error || 'Ungültige Anmeldedaten');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (modern) setLoginError('Anmeldung nicht möglich. Bitte versuche es gleich noch einmal.');
      else showError('Fehler', 'Ein unerwarteter Fehler ist aufgetreten');
    } finally {
      setLoading(false);
      setSyncProgress(null);
    }
  };

  const handlePasskeyLogin = async () => {
    setLoginError('');
    setPasskeyLoading(true);
    passkeyButtonActive.current = true;
    try {
      const result = await loginWithPasskey();
      if (result.success) handleLoginSuccess(result);
      else if (!result.cancelled) {
        if (modern) setLoginError(result.error);
        else showError('Anmeldung fehlgeschlagen', result.error);
      }
    } finally {
      passkeyButtonActive.current = false;
      setPasskeyLoading(false);
      setAutofillRun((n) => n + 1);
    }
  };

  // Passkey-Autovervollständigung: gespeicherte Passkeys erscheinen in der Vorschlagsliste des E-Mail-Felds.
  // Die Abfrage wird alle 4 Minuten erneuert, weil die Challenge auf dem Server nach 5 Minuten verfällt.
  useEffect(() => {
    if (checkingAuth || !isOnline || !passkeyAvailable) return undefined;
    let active = true;
    let refresh;
    (async () => {
      if (!(await passkeyAutofillSupported())) return;
      while (active && !passkeyButtonActive.current) {
        refresh = setTimeout(stopPasskeyAutofill, 4 * 60 * 1000);
        const result = await loginWithPasskeyAutofill();
        clearTimeout(refresh);
        if (!active || passkeyButtonActive.current) return;
        if (result.success) {
          handleLoginSuccess(result);
          return;
        }
        if (result.beforePrompt) return;
        if (result.error) {
          if (modern) setLoginError(result.error);
          else showError('Anmeldung fehlgeschlagen', result.error);
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    })();
    return () => {
      active = false;
      clearTimeout(refresh);
      if (!passkeyButtonActive.current) stopPasskeyAutofill();
    };
  }, [checkingAuth, isOnline, autofillRun]);

  // Fehler wirft completeProfile weiter – das Modal zeigt sie an
  const handleProfileComplete = async (firstname, lastname) => {
    await completeProfile(firstname, lastname);
    setShowProfileModal(false);
    showSuccess('Profil gespeichert', 'Ihr Profil wurde erfolgreich vervollständigt');

    // Push-Benachrichtigungen anfragen wenn unterstützt und noch nicht gefragt
    if (isPushSupported() && !wasPermissionAsked() && getNotificationPermission() === 'default') {
      setTimeout(async () => {
        const pushResult = await subscribeToPush();
        if (pushResult.success) {
          console.log('✅ Push-Benachrichtigungen aktiviert');
        }
      }, 1500);
    }
    
    // Synchronisation läuft nach dem Wechsel in die App im Hintergrund (SyncContext)
    requestSyncAfterLogin();
    navigate('/');
  };

  const handleForgotPassword = async (email) => {
    try {
      const response = await apiService.post('/auth/request-reset', { email });
      setShowForgotPasswordModal(false);
      showSuccess(
        'E-Mail gesendet', 
        'Falls ein Account mit dieser E-Mail existiert, wurde eine E-Mail mit einem Reset-Link gesendet.'
      );
    } catch (error) {
      showError('Fehler', error.message || 'Passwort-Reset-Anfrage fehlgeschlagen');
    }
  };

  if (modern) {
    return (
      <>
        <ModalComponent />
        <LoginView
          checkingAuth={checkingAuth}
          isOnline={isOnline}
          username={username}
          setUsername={setUsername}
          password={password}
          setPassword={setPassword}
          loading={loading}
          error={loginError}
          syncProgress={syncProgress}
          onSubmit={handleSubmit}
          onForgotPassword={() => setShowForgotPasswordModal(true)}
          passkeyAvailable={passkeyAvailable}
          passkeyLoading={passkeyLoading}
          onPasskeyLogin={handlePasskeyLogin}
          version={typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : ''}
        />
        <ProfileCompleteModal isOpen={showProfileModal} onComplete={handleProfileComplete} />
        <ForgotPasswordModal
          isOpen={showForgotPasswordModal}
          onClose={() => setShowForgotPasswordModal(false)}
          onSubmit={handleForgotPassword}
        />
      </>
    );
  }

  if (checkingAuth) {
    return (
      <div className="login-page">
        <ModalComponent />
        <div className="login-container">
          <div className="loading">Überprüfe Anmeldung...</div>
        </div>
      </div>
    );
  }

  // Wenn offline, zeige spezielle Offline-Seite
  if (!isOnline) {
    return (
      <div className="login-page">
        <ModalComponent />
        <div className="login-container offline-container">
          <div className="login-header">
            <div className="login-icon offline-icon">📡❌</div>
            <h1>Keine Internetverbindung</h1>
            <p>Anmeldung nicht möglich</p>
          </div>

          <div className="offline-message">
            <div className="message-box warning">
              <h3>🔒 Anmeldung erfordert Internetverbindung</h3>
              <p>
                Aus Sicherheitsgründen ist eine Anmeldung nur mit aktiver Internetverbindung möglich. 
                Dies stellt sicher, dass Ihre Zugangsdaten überprüft werden können.
              </p>
            </div>

            <div className="message-box info">
              <h3>ℹ️ So funktioniert die Offline-Nutzung</h3>
              <ul>
                <li>✅ Nach erfolgreicher Anmeldung bleibt Ihre Sitzung <strong>7 Tage lang gültig</strong></li>
                <li>✅ In diesen 7 Tagen können Sie die App auch <strong>ohne Internet</strong> nutzen</li>
                <li>✅ Alle Daten werden lokal auf Ihrem Gerät gespeichert</li>
                <li>⚠️ Nach Ablauf der 7 Tage ist eine erneute Anmeldung mit Internet erforderlich</li>
              </ul>
            </div>

            <div className="message-box steps">
              <h3>📋 Nächste Schritte</h3>
              <p>Bitte stellen Sie eine Internetverbindung her und laden Sie diese Seite neu:</p>
              <ol>
                <li>WLAN oder mobile Daten aktivieren</li>
                <li>Warten bis die Verbindung hergestellt ist</li>
                <li>Seite neu laden (nach unten ziehen oder F5)</li>
                <li>Anmelden und 7 Tage offline arbeiten</li>
              </ol>
            </div>

            <div className={`connection-status ${isOnline ? 'online' : 'offline'}`}>
              <span className="status-icon">{isOnline ? '✅' : '❌'}</span>
              <span className="status-text">
                {isOnline ? 'Verbindung hergestellt - Seite wird neu geladen...' : 'Keine Verbindung'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <ModalComponent />
      <div className="login-container">
        <div className="login-header">
          <div className="login-icon">🔧</div>
          <h1>Ersatzteil-Finder</h1>
          <p>Anmeldung für Techniker</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Benutzername</label>
            <input
              type="text"
              id="username"
              autoComplete="username webauthn"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="email@beispiel.de"
              required
              autoFocus
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Passwort</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? '⏳ Anmeldung läuft...' : '🔓 Anmelden'}
          </button>

          {passkeyAvailable && (
            <button
              type="button"
              className="login-button login-button--passkey"
              onClick={handlePasskeyLogin}
              disabled={loading || passkeyLoading}
            >
              {passkeyLoading ? '⏳ Warte auf Passkey...' : '🔑 Mit Passkey anmelden'}
            </button>
          )}

          <div className="forgot-password-link">
            <button 
              type="button" 
              onClick={() => setShowForgotPasswordModal(true)}
              className="link-button"
              disabled={loading}
            >
              Passwort vergessen?
            </button>
          </div>

          <div className="login-info">
            <div className="info-badge">
              <span className="badge-icon">🔒</span>
              <span>Sitzung gültig für 7 Tage</span>
            </div>
            <div className="info-badge">
              <span className="badge-icon">📱</span>
              <span>Auch offline nutzbar</span>
            </div>
          </div>
        </form>

        <div className="login-footer">
          <a href="https://bentomax.de/impressum" target="_blank" rel="noopener noreferrer">Impressum</a>
          {' & '}
          <Link to="/datenschutz">Datenschutz</Link>
        </div>
      </div>

      <ProfileCompleteModal
        isOpen={showProfileModal}
        onComplete={handleProfileComplete}
      />

      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
        onSubmit={handleForgotPassword}
      />
    </div>
  );
}
